import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import IncomingRequests from '../components/IncomingRequests';
import { PaymentStatusInline } from '../components/PaymentStatus';
import AssignProviderModal from '../components/AssignProviderModal';
import { AccountTypeBadge } from '../components/Header';
import LiveProviderMap from '../components/LiveProviderMap';
import ProviderManagement from '../components/ProviderManagement';
import AdminAdApproval from '../components/AdminAdApproval';
import AdminOrderManagement from '../components/AdminOrderManagement';
import PayoutApprovalDashboard from '../components/PayoutApprovalDashboard';
import AutoPartsOrderApproval from '../components/AutoPartsOrderApproval';
import FoodDeliveryAdmin from '../components/FoodDeliveryAdmin';
import WebhookQueueMonitor from '../components/WebhookQueueMonitor';
import { ExportPDFButton } from '../components/ExportPDFButton';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Car, Users, FileText, Bell, Settings, DollarSign, 
  Check, X, Eye, Loader2, Plus, Edit, Trash2, 
  Clock, Mail, Phone, MapPin, Wrench, Truck, Shield,
  BarChart3, Palette, Briefcase, Star, RefreshCw, Timer, UserPlus, AlertCircle, User, Navigation,
  Sparkles, Package, Wifi
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [pendingProviders, setPendingProviders] = useState([]);
  const [services, setServices] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  
  // Live Activity & All Requests states
  const [liveActivity, setLiveActivity] = useState({ all_users: [], summary: {} });
  const [allRequests, setAllRequests] = useState({ all_requests: [], summary: {} });
  const [activeUsersCount, setActiveUsersCount] = useState({ total_active: 0, online_providers: 0 });
  const [requestFilter, setRequestFilter] = useState('all'); // Filter for clickable stats
  
  // Assign Provider Modal state
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  
  // User management states
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    user_type: 'customer',
    is_admin: false
  });
  
  // Service form
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceForm, setServiceForm] = useState({
    service_type: '',
    name: '',
    price: '',
    category: 'roadside',
    description: ''
  });

  const fetchLiveActivity = useCallback(async () => {
    try {
      const headers = { ...getAuthHeaders() };
      const response = await axios.get(`${API}/admin/live-activity`, { headers, withCredentials: true });
      setLiveActivity(response.data || { all_users: [], summary: {} });
    } catch (error) {
      console.error('Failed to fetch live activity:', error);
    }
  }, [getAuthHeaders]);

  const fetchAllRequests = useCallback(async () => {
    try {
      const headers = { ...getAuthHeaders() };
      const response = await axios.get(`${API}/admin/all-requests`, { headers, withCredentials: true });
      setAllRequests(response.data || { all_requests: [], summary: {} });
    } catch (error) {
      console.error('Failed to fetch all requests:', error);
    }
  }, [getAuthHeaders]);

  const fetchActiveUsersCount = useCallback(async () => {
    try {
      const headers = { ...getAuthHeaders() };
      const response = await axios.get(`${API}/admin/active-users-count`, { headers, withCredentials: true });
      setActiveUsersCount(response.data || { total_active: 0, online_providers: 0 });
    } catch (error) {
      console.error('Failed to fetch active users count:', error);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (!user?.is_admin) {
      toast.error('Admin access required');
      navigate('/');
      return;
    }
    fetchAllData();
    fetchLiveActivity();
    fetchAllRequests();
    fetchActiveUsersCount();
    
    // Auto-refresh every 10 seconds
    const interval = setInterval(() => {
      fetchLiveActivity();
      fetchAllRequests();
      fetchActiveUsersCount();
    }, 10000);
    
    return () => clearInterval(interval);
  }, [user, navigate, fetchLiveActivity, fetchAllRequests]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const headers = { ...getAuthHeaders() };
      
      const [appsRes, pendingProvidersRes, servicesRes, notifsRes, usersRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/applications`, { headers, withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/pending-providers`, { headers, withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/services/catalog`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/notifications`, { headers, withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/users`, { headers, withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/stats`, { headers, withCredentials: true }).catch(() => ({ data: {} }))
      ]);
      
      setApplications(appsRes.data || []);
      setPendingProviders(pendingProvidersRes.data || []);
      setServices(servicesRes.data || []);
      setNotifications(notifsRes.data || []);
      setUsers(usersRes.data || []);
      setStats(statsRes.data || {});
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProviderAction = async (userId, action, reason = '') => {
    try {
      await axios.put(
        `${API}/admin/providers/${userId}/approve`,
        { action, reason },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      toast.success(`Provider ${action}d successfully`);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} provider`);
    }
  };

  const handleApplicationAction = async (applicationId, status) => {
    try {
      await axios.put(
        `${API}/admin/applications/${applicationId}`,
        { status },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      toast.success(`Application ${status}`);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update application');
    }
  };

  const handleServiceSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/admin/services`,
        {
          ...serviceForm,
          price: parseFloat(serviceForm.price)
        },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      toast.success(editingService ? 'Service updated' : 'Service created');
      setShowServiceForm(false);
      setEditingService(null);
      setServiceForm({ service_type: '', name: '', price: '', category: 'roadside', description: '' });
      fetchAllData();
    } catch (error) {
      toast.error('Failed to save service');
    }
  };

  const handleDeleteService = async (serviceType) => {
    if (!window.confirm('Are you sure you want to deactivate this service?')) return;
    try {
      await axios.delete(
        `${API}/admin/services/${serviceType}`,
        { headers: getAuthHeaders(), withCredentials: true }
      );
      toast.success('Service deactivated');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete service');
    }
  };

  const editService = (service) => {
    setEditingService(service);
    setServiceForm({
      service_type: service.service_type,
      name: service.name,
      price: service.price.toString(),
      category: service.category,
      description: service.description || ''
    });
    setShowServiceForm(true);
  };

  const handleToggleAdmin = async (userId, makeAdmin) => {
    try {
      await axios.put(
        `${API}/admin/users/${userId}/admin`,
        { is_admin: makeAdmin },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      toast.success(`Admin status updated`);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  if (!user?.is_admin) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
      </div>
    );
  }

  const pendingApps = applications.filter(a => a.status === 'pending');

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-slate-50 to-blue-50/20" data-testid="admin-dashboard">
      {/* Header */}
      <header className="glass-header border-b border-gray-200/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-blue-600 rounded-sm flex items-center justify-center">
                <Car className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900 font-['Chivo']">Jiffys Admin</span>
            </Link>
            <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-medium">
              Admin Panel
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-gray-600">{user.email}</span>
            <ExportPDFButton reportType="owner" label="Export PDF" />
            <Link to="/admin/analytics">
              <Button variant="outline" size="sm">
                <BarChart3 className="w-4 h-4 mr-2" />
                Analytics
              </Button>
            </Link>
            <Link to="/admin/design">
              <Button variant="outline" size="sm">
                <Palette className="w-4 h-4 mr-2" />
                Design
              </Button>
            </Link>
            <Link to="/customer">
              <Button variant="outline" size="sm">Exit Admin</Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8 pb-20 sm:pb-8">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 sm:gap-4 mb-4 sm:mb-8 animate-fade-in-up">
          <Card className="glass hover-lift transition-all">
            <CardContent className="py-3 sm:py-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-9 h-9 sm:w-10 sm:h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Pending</p>
                  <p className="text-lg sm:text-xl font-bold text-gray-900">{pendingApps.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="glass hover-lift transition-all">
            <CardContent className="py-3 sm:py-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-9 h-9 sm:w-10 sm:h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Users</p>
                  <p className="text-lg sm:text-xl font-bold text-gray-900">{users.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="glass-success hover-lift transition-all border-green-200/50">
            <CardContent className="py-3 sm:py-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-9 h-9 sm:w-10 sm:h-10 bg-green-200 rounded-lg flex items-center justify-center">
                  <User className="w-4 h-4 sm:w-5 sm:h-5 text-green-700" />
                </div>
                <div>
                  <p className="text-xs text-green-700 font-medium">Active Now</p>
                  <p className="text-lg sm:text-xl font-bold text-green-800">{activeUsersCount.online_providers || liveActivity?.total_active || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="glass hover-lift transition-all">
            <CardContent className="py-3 sm:py-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-9 h-9 sm:w-10 sm:h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Services</p>
                  <p className="text-lg sm:text-xl font-bold text-gray-900">{services.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="glass hover-lift transition-all">
            <CardContent className="py-3 sm:py-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-9 h-9 sm:w-10 sm:h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Bell className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Notifs</p>
                  <p className="text-lg sm:text-xl font-bold text-gray-900">{notifications.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Tabs defaultValue="accept-jobs" className="space-y-4 sm:space-y-6">
          <TabsList className="glass p-1 rounded-xl flex flex-wrap h-auto gap-1 overflow-x-auto">
            <TabsTrigger value="accept-jobs" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3">
              <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Accept Jobs</span>
              <span className="sm:hidden">Jobs</span>
            </TabsTrigger>
            <TabsTrigger value="live-activity" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3">
              <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Live Activity</span>
              <span className="sm:hidden">Live</span>
              {liveActivity?.total_active > 0 && (
                <span className="bg-green-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                  {liveActivity.total_active}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="all-requests" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3">
              <Briefcase className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">All Requests</span>
              <span className="sm:hidden">Requests</span>
              {allRequests?.summary?.total_pending > 0 && (
                <span className="bg-cyan-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                  {allRequests.summary.total_pending}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="providers" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3">
              <Shield className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Provider Approvals</span>
              <span className="sm:hidden">Approvals</span>
              {pendingProviders.length > 0 && (
                <span className="bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                  {pendingProviders.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="applications" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3">
              <FileText className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Applications</span>
              <span className="sm:hidden">Apps</span>
              {pendingApps.length > 0 && (
                <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                  {pendingApps.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="services" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3">
              <Settings className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Services</span>
              <span className="sm:hidden">Svc</span>
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3">
              <Users className="w-3 h-3 sm:w-4 sm:h-4" />
              Users
            </TabsTrigger>
            <TabsTrigger value="notifications" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3">
              <Bell className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Activity Log</span>
              <span className="sm:hidden">Log</span>
            </TabsTrigger>
            <TabsTrigger value="provider-map" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3">
              <MapPin className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Live Map</span>
              <span className="sm:hidden">Map</span>
            </TabsTrigger>
            <TabsTrigger value="provider-mgmt" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3">
              <UserPlus className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Provider Mgmt</span>
              <span className="sm:hidden">Mgmt</span>
            </TabsTrigger>
            <TabsTrigger value="ad-approval" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3">
              <Sparkles className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Ad Approval</span>
              <span className="sm:hidden">Ads</span>
            </TabsTrigger>
            <TabsTrigger value="orders" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3">
              <Package className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Orders</span>
              <span className="sm:hidden">Orders</span>
            </TabsTrigger>
            <TabsTrigger value="payouts" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3">
              <DollarSign className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Payouts</span>
              <span className="sm:hidden">Pay</span>
            </TabsTrigger>
            <TabsTrigger value="auto-parts" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3">
              <Wrench className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Auto Parts</span>
              <span className="sm:hidden">Parts</span>
            </TabsTrigger>
            <TabsTrigger value="food-scraper" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3">
              <Sparkles className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">AI Scraper</span>
              <span className="sm:hidden">AI</span>
            </TabsTrigger>
            <TabsTrigger value="webhooks" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3">
              <Wifi className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Webhooks</span>
              <span className="sm:hidden">WH</span>
            </TabsTrigger>
          </TabsList>

          {/* Accept Jobs Tab */}
          <TabsContent value="accept-jobs">
            <div className="space-y-6">
              <IncomingRequests />
              
              {/* Option to assign jobs to providers */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-indigo-600" />
                    Assign Jobs to Providers
                  </CardTitle>
                  <CardDescription>As an admin, you can accept jobs yourself or assign them to specific providers</CardDescription>
                </CardHeader>
              </Card>
            </div>
          </TabsContent>

          {/* Live Activity Tab */}
          <TabsContent value="live-activity">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="w-5 h-5 text-green-600" />
                  Live User Activity
                  <Badge className="ml-2 bg-green-100 text-green-700">Auto-refreshes every 10s</Badge>
                </CardTitle>
                <CardDescription>Real-time view of all active users and their current pages</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Activity Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="p-3 bg-blue-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-blue-600">{liveActivity?.summary?.total_customers || 0}</p>
                    <p className="text-xs text-blue-700">Customers Online</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-600">{liveActivity?.summary?.total_mechanics || 0}</p>
                    <p className="text-xs text-green-700">Mechanics Online</p>
                  </div>
                  <div className="p-3 bg-orange-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-orange-600">{liveActivity?.summary?.total_roadside || 0}</p>
                    <p className="text-xs text-orange-700">Roadside Techs Online</p>
                  </div>
                  <div className="p-3 bg-purple-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-purple-600">{liveActivity?.summary?.total_admins || 0}</p>
                    <p className="text-xs text-purple-700">Admins Online</p>
                  </div>
                </div>

                {/* Active Users List */}
                {liveActivity?.all_users?.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No active users right now</p>
                ) : (
                  <div className="space-y-3">
                    {liveActivity?.all_users?.map((activity, idx) => (
                      <div 
                        key={idx}
                        className={`p-4 rounded-lg border flex items-center justify-between ${
                          activity.user_type === 'owner' ? 'bg-purple-50 border-purple-200' :
                          activity.is_admin ? 'bg-indigo-50 border-indigo-200' :
                          activity.user_type === 'mechanic' ? 'bg-blue-50 border-blue-200' :
                          activity.user_type === 'roadside_tech' ? 'bg-green-50 border-green-200' :
                          'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="relative">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              activity.user_type === 'owner' ? 'bg-purple-100' :
                              activity.is_admin ? 'bg-indigo-100' :
                              activity.user_type === 'mechanic' ? 'bg-blue-100' :
                              activity.user_type === 'roadside_tech' ? 'bg-green-100' :
                              'bg-gray-100'
                            }`}>
                              {activity.user_type === 'owner' ? <Shield className="w-5 h-5 text-purple-600" /> :
                               activity.is_admin ? <Shield className="w-5 h-5 text-indigo-600" /> :
                               activity.user_type === 'mechanic' ? <Wrench className="w-5 h-5 text-blue-600" /> :
                               activity.user_type === 'roadside_tech' ? <Truck className="w-5 h-5 text-green-600" /> :
                               <Users className="w-5 h-5 text-gray-600" />}
                            </div>
                            <span className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{activity.user_name}</p>
                            <p className="text-xs text-gray-500">{activity.user_email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge className={
                            activity.current_page?.includes('owner') || activity.current_page?.includes('admin') ? 'bg-purple-100 text-purple-700' :
                            activity.current_page?.includes('mechanic') ? 'bg-blue-100 text-blue-700' :
                            activity.current_page?.includes('customer') ? 'bg-green-100 text-green-700' :
                            activity.current_page?.includes('marketplace') ? 'bg-orange-100 text-orange-700' :
                            'bg-gray-100 text-gray-700'
                          }>
                            {activity.page_title || activity.current_page}
                          </Badge>
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(activity.last_active).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* All Requests Tab */}
          <TabsContent value="all-requests">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-cyan-600" />
                  All Service Requests
                  <Badge className="ml-2 bg-cyan-100 text-cyan-700">All Customers</Badge>
                </CardTitle>
                <CardDescription>View all pending and active service requests from all customers</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Request Summary - Clickable Stats */}
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
                  <button 
                    onClick={() => setRequestFilter('all')}
                    className={`p-3 rounded-lg text-center transition-all ${
                      requestFilter === 'all' 
                        ? 'bg-gray-200 ring-2 ring-gray-400' 
                        : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                    data-testid="filter-all"
                  >
                    <p className="text-2xl font-bold text-gray-700">{allRequests?.all_requests?.length || 0}</p>
                    <p className="text-xs text-gray-600">All</p>
                  </button>
                  <button 
                    onClick={() => setRequestFilter('pending')}
                    className={`p-3 rounded-lg text-center transition-all ${
                      requestFilter === 'pending' 
                        ? 'bg-yellow-200 ring-2 ring-yellow-400' 
                        : 'bg-yellow-50 hover:bg-yellow-100'
                    }`}
                    data-testid="filter-pending"
                  >
                    <p className="text-2xl font-bold text-yellow-600">{allRequests?.summary?.total_pending || 0}</p>
                    <p className="text-xs text-yellow-700">Pending</p>
                  </button>
                  <button 
                    onClick={() => setRequestFilter('accepted')}
                    className={`p-3 rounded-lg text-center transition-all ${
                      requestFilter === 'accepted' 
                        ? 'bg-blue-200 ring-2 ring-blue-400' 
                        : 'bg-blue-50 hover:bg-blue-100'
                    }`}
                    data-testid="filter-accepted"
                  >
                    <p className="text-2xl font-bold text-blue-600">{allRequests?.summary?.total_accepted || 0}</p>
                    <p className="text-xs text-blue-700">Accepted</p>
                  </button>
                  <button 
                    onClick={() => setRequestFilter('in_progress')}
                    className={`p-3 rounded-lg text-center transition-all ${
                      requestFilter === 'in_progress' 
                        ? 'bg-orange-200 ring-2 ring-orange-400' 
                        : 'bg-orange-50 hover:bg-orange-100'
                    }`}
                    data-testid="filter-in-progress"
                  >
                    <p className="text-2xl font-bold text-orange-600">{allRequests?.summary?.total_in_progress || 0}</p>
                    <p className="text-xs text-orange-700">In Progress</p>
                  </button>
                  <button 
                    onClick={() => setRequestFilter('completed')}
                    className={`p-3 rounded-lg text-center transition-all ${
                      requestFilter === 'completed' 
                        ? 'bg-green-200 ring-2 ring-green-400' 
                        : 'bg-green-50 hover:bg-green-100'
                    }`}
                    data-testid="filter-completed"
                  >
                    <p className="text-2xl font-bold text-green-600">{allRequests?.summary?.total_completed || 0}</p>
                    <p className="text-xs text-green-700">Completed</p>
                  </button>
                  <button 
                    onClick={() => setRequestFilter('cancelled')}
                    className={`p-3 rounded-lg text-center transition-all ${
                      requestFilter === 'cancelled' 
                        ? 'bg-red-200 ring-2 ring-red-400' 
                        : 'bg-red-50 hover:bg-red-100'
                    }`}
                    data-testid="filter-cancelled"
                  >
                    <p className="text-2xl font-bold text-red-600">{allRequests?.summary?.total_cancelled || 0}</p>
                    <p className="text-xs text-red-700">Cancelled</p>
                  </button>
                </div>

                {/* Requests List - Filtered */}
                {(() => {
                  const filteredRequests = requestFilter === 'all' 
                    ? allRequests?.all_requests 
                    : allRequests?.all_requests?.filter(req => req.status === requestFilter);
                  
                  if (!filteredRequests || filteredRequests.length === 0) {
                    return <p className="text-gray-500 text-center py-8">No {requestFilter === 'all' ? '' : requestFilter} requests found</p>;
                  }
                  
                  return (
                  <div className="space-y-4">
                    {filteredRequests.slice(0, 50).map((req) => (
                      <div 
                        key={req.request_id}
                        className={`p-4 rounded-lg border cursor-pointer hover:shadow-md transition-shadow ${
                          req.status === 'pending' ? 'bg-yellow-50 border-yellow-200' :
                          req.status === 'accepted' ? 'bg-blue-50 border-blue-200' :
                          req.status === 'in_progress' ? 'bg-orange-50 border-orange-200' :
                          req.status === 'completed' ? 'bg-green-50 border-green-200' :
                          'bg-red-50 border-red-200'
                        }`}
                        onClick={() => navigate(`/admin/job/${req.request_id}`)}
                        data-testid={`request-card-${req.request_id}`}
                      >
                        <div className="flex items-start gap-4">
                          {/* Customer Photo */}
                          <div className="w-14 h-14 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                            {req.customer_details?.picture || req.customer_picture ? (
                              <img 
                                src={req.customer_details?.picture || req.customer_picture} 
                                alt={req.customer_details?.name || 'Customer'}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <User className="w-6 h-6 text-gray-400" />
                            )}
                          </div>
                          
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <Badge className={
                                req.status === 'pending' ? 'bg-yellow-600' :
                                req.status === 'accepted' ? 'bg-blue-600' :
                                req.status === 'in_progress' ? 'bg-orange-600' :
                                req.status === 'completed' ? 'bg-green-600' :
                                'bg-red-600'
                              }>
                                {req.status}
                              </Badge>
                              {/* Payment Status */}
                              <PaymentStatusInline 
                                status={req.payment_status || 'pending'} 
                                type="customer" 
                              />
                              {req.mechanic_id && (
                                <PaymentStatusInline 
                                  status={req.provider_payment_status || 'pending'} 
                                  type="provider" 
                                />
                              )}
                              {req.owner_discount_applied && (
                                <Badge className="bg-purple-100 text-purple-700">OWNER - FREE</Badge>
                              )}
                            </div>
                            <h3 className="font-semibold text-lg">{req.service_name || req.service_type}</h3>
                            
                            {/* Customer Details */}
                            <div className="flex items-center gap-2 mt-2">
                              <Users className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-gray-600">
                                Customer: {req.customer_details?.name || req.customer_name || 'Unknown'}
                              </span>
                              {req.customer_details?.email && (
                                <span className="text-xs text-gray-400">({req.customer_details.email})</span>
                              )}
                            </div>
                            
                            {/* Provider Details */}
                            {req.provider_details && (
                              <div className="flex items-center gap-2 mt-1">
                                <Wrench className="w-4 h-4 text-blue-400" />
                                <span className="text-sm text-blue-600">
                                  Assigned: {req.provider_details.name}
                                </span>
                                <div className="flex items-center gap-1">
                                  <Star className="w-3 h-3 text-yellow-500 fill-current" />
                                  <span className="text-xs text-gray-500">{req.provider_details.rating || 5.0}</span>
                                </div>
                              </div>
                            )}
                            
                            <div className="flex items-center gap-4 mt-2 text-sm flex-wrap">
                              <span className="flex items-center gap-1 text-gray-600">
                                <Car className="w-4 h-4" />
                                {req.vehicle_year} {req.vehicle_make} {req.vehicle_model}
                              </span>
                              <span className="flex items-center gap-1 text-green-600 font-semibold">
                                <DollarSign className="w-4 h-4" />
                                ${req.total_price || 0}
                              </span>
                              <span className="flex items-center gap-1 text-gray-400">
                                <Clock className="w-4 h-4" />
                                {new Date(req.created_at).toLocaleString()}
                              </span>
                            </div>
                            
                            {/* Full Customer Address */}
                            {req.location?.address && (
                              <div className="flex items-start gap-1 mt-2 text-sm text-gray-600 bg-gray-100 p-2 rounded">
                                <MapPin className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                                <div>
                                  <p className="font-medium">{req.location.address}</p>
                                  {req.location.lat && (
                                    <p className="text-xs text-gray-400">
                                      GPS: {req.location.lat.toFixed(4)}, {req.location.lng.toFixed(4)}
                                    </p>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            {/* Action Buttons */}
                            <div className="flex gap-2 mt-3">
                              {!req.mechanic_id && req.status === 'pending' && (
                                <Button
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedRequest(req);
                                    setAssignModalOpen(true);
                                  }}
                                  className="bg-blue-600 hover:bg-blue-700"
                                  data-testid={`assign-btn-${req.request_id}`}
                                >
                                  <UserPlus className="w-4 h-4 mr-1" />
                                  Assign Provider
                                </Button>
                              )}
                              {req.location?.lat && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(`https://www.google.com/maps/dir/?api=1&destination=${req.location.lat},${req.location.lng}`, '_blank');
                                  }}
                                  data-testid={`navigate-btn-${req.request_id}`}
                                >
                                  <Navigation className="w-4 h-4 mr-1" />
                                  Navigate
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  );
                })()}
                
                {/* Assign Provider Modal */}
                <AssignProviderModal
                  isOpen={assignModalOpen}
                  onClose={() => {
                    setAssignModalOpen(false);
                    setSelectedRequest(null);
                  }}
                  requestId={selectedRequest?.request_id}
                  serviceName={selectedRequest?.service_name || selectedRequest?.service_type}
                  customerLocation={selectedRequest?.location}
                  onAssigned={() => {
                    fetchAllRequests();
                    setAssignModalOpen(false);
                    setSelectedRequest(null);
                  }}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Provider Approvals Tab */}
          <TabsContent value="providers">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-orange-600" />
                  Pending Provider Registrations
                </CardTitle>
              </CardHeader>
              <CardContent>
                {pendingProviders.length === 0 ? (
                  <div className="text-center py-8">
                    <Check className="w-12 h-12 text-green-500 mx-auto mb-3" />
                    <p className="text-gray-500">No pending provider registrations</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingProviders.map((provider) => (
                      <div 
                        key={provider.user_id}
                        className="p-6 rounded-lg border-2 border-orange-200 bg-orange-50"
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                              provider.user_type === 'mechanic' ? 'bg-blue-100' : 
                              provider.user_type === 'admin' ? 'bg-purple-100' : 'bg-green-100'
                            }`}>
                              {provider.user_type === 'mechanic' ? (
                                <Wrench className="w-6 h-6 text-blue-600" />
                              ) : provider.user_type === 'admin' ? (
                                <Shield className="w-6 h-6 text-purple-600" />
                              ) : (
                                <Truck className="w-6 h-6 text-green-600" />
                              )}
                            </div>
                            <div>
                              <h3 className="font-semibold text-gray-900">{provider.name}</h3>
                              <p className="text-sm text-gray-500 capitalize">
                                {provider.user_type === 'mechanic' ? 'Mobile Mechanic' : 
                                 provider.user_type === 'roadside_tech' ? 'Roadside Technician' : 
                                 provider.user_type === 'admin' ? 'Administrator' : provider.user_type}
                              </p>
                            </div>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            provider.user_type === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-orange-100 text-orange-700'
                          }`}>
                            {provider.user_type === 'admin' ? 'ADMIN PENDING' : 'PENDING APPROVAL'}
                          </span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                          <div className="flex items-center gap-2 text-gray-600">
                            <Mail className="w-4 h-4" />
                            <span className="text-sm">{provider.email}</span>
                          </div>
                          {provider.phone && (
                            <div className="flex items-center gap-2 text-gray-600">
                              <Phone className="w-4 h-4" />
                              <span className="text-sm">{provider.phone}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-2 text-gray-600">
                            <Clock className="w-4 h-4" />
                            <span className="text-sm">
                              {new Date(provider.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>

                        {/* Skills */}
                        {provider.skills?.length > 0 && (
                          <div className="mb-4">
                            <p className="text-sm font-medium text-gray-700 mb-2">Skills:</p>
                            <div className="flex flex-wrap gap-2">
                              {provider.skills.map((skill, idx) => (
                                <span 
                                  key={idx}
                                  className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs"
                                >
                                  {skill}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Certifications */}
                        {provider.certifications?.length > 0 && (
                          <div className="mb-4">
                            <p className="text-sm font-medium text-gray-700 mb-2">Certifications:</p>
                            <div className="flex flex-wrap gap-2">
                              {provider.certifications.map((cert, idx) => (
                                <span 
                                  key={idx}
                                  className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs"
                                >
                                  {cert}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="flex gap-3 pt-4 border-t">
                          <Button
                            onClick={() => handleProviderAction(provider.user_id, 'approve')}
                            className="flex-1 bg-green-600 hover:bg-green-700"
                          >
                            <Check className="w-4 h-4 mr-2" />
                            Approve
                          </Button>
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button variant="destructive" className="flex-1">
                                <X className="w-4 h-4 mr-2" />
                                Reject
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Reject Provider Application</DialogTitle>
                              </DialogHeader>
                              <form onSubmit={(e) => {
                                e.preventDefault();
                                const reason = e.target.reason.value;
                                handleProviderAction(provider.user_id, 'reject', reason);
                              }}>
                                <div className="space-y-4">
                                  <div>
                                    <Label htmlFor="reason">Rejection Reason (Optional)</Label>
                                    <Textarea
                                      id="reason"
                                      name="reason"
                                      placeholder="Provide a reason for rejection..."
                                      className="mt-1"
                                    />
                                  </div>
                                  <Button type="submit" variant="destructive" className="w-full">
                                    Confirm Rejection
                                  </Button>
                                </div>
                              </form>
                            </DialogContent>
                          </Dialog>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Applications Tab */}
          <TabsContent value="applications">
            <Card>
              <CardHeader>
                <CardTitle>Provider Applications</CardTitle>
              </CardHeader>
              <CardContent>
                {applications.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No applications yet</p>
                ) : (
                  <div className="space-y-4">
                    {applications.map((app) => (
                      <div 
                        key={app.application_id}
                        className={`p-6 rounded-lg border-2 ${
                          app.status === 'pending' ? 'border-yellow-200 bg-yellow-50' :
                          app.status === 'approved' ? 'border-green-200 bg-green-50' :
                          'border-red-200 bg-red-50'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                              app.provider_type === 'mechanic' ? 'bg-blue-100' : 'bg-red-100'
                            }`}>
                              {app.provider_type === 'mechanic' ? (
                                <Wrench className="w-6 h-6 text-blue-600" />
                              ) : (
                                <Truck className="w-6 h-6 text-red-600" />
                              )}
                            </div>
                            <div>
                              <h3 className="font-semibold text-gray-900">{app.full_name}</h3>
                              <p className="text-sm text-gray-500 capitalize">
                                {app.provider_type === 'mechanic' ? 'Mobile Mechanic' : 'Roadside Provider'}
                              </p>
                            </div>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            app.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            app.status === 'approved' ? 'bg-green-100 text-green-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {app.status.toUpperCase()}
                          </span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                          <div className="flex items-center gap-2 text-gray-600">
                            <Mail className="w-4 h-4" />
                            <span className="text-sm">{app.email}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <Phone className="w-4 h-4" />
                            <span className="text-sm">{app.phone}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <Clock className="w-4 h-4" />
                            <span className="text-sm">{app.experience_years} years experience</span>
                          </div>
                        </div>

                        {app.specialties && app.specialties.length > 0 && (
                          <div className="mb-4">
                            <p className="text-sm text-gray-500 mb-2">Specialties:</p>
                            <div className="flex flex-wrap gap-2">
                              {app.specialties.map((s, i) => (
                                <span key={i} className="bg-gray-200 text-gray-700 px-2 py-1 rounded text-sm">
                                  {s}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {app.about && (
                          <div className="mb-4 p-3 bg-white rounded-lg">
                            <p className="text-sm text-gray-600">{app.about}</p>
                          </div>
                        )}

                        <div className="flex flex-wrap gap-4 mb-4">
                          {app.photo_urls?.length > 0 && (
                            <span className="text-sm text-blue-600">📷 {app.photo_urls.length} photos</span>
                          )}
                          {app.video_urls?.length > 0 && (
                            <span className="text-sm text-blue-600">🎥 {app.video_urls.length} videos</span>
                          )}
                          {app.id_document_url && (
                            <span className="text-sm text-green-600">✓ ID uploaded</span>
                          )}
                          {app.insurance_url && (
                            <span className="text-sm text-green-600">✓ Insurance uploaded</span>
                          )}
                        </div>

                        {app.status === 'pending' && (
                          <div className="flex gap-3 pt-4 border-t">
                            <Button
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => handleApplicationAction(app.application_id, 'approved')}
                            >
                              <Check className="w-4 h-4 mr-2" />
                              Approve
                            </Button>
                            <Button
                              variant="outline"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => handleApplicationAction(app.application_id, 'rejected')}
                            >
                              <X className="w-4 h-4 mr-2" />
                              Reject
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Services Tab */}
          <TabsContent value="services">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Service Management</CardTitle>
                <Button 
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={() => {
                    setEditingService(null);
                    setServiceForm({ service_type: '', name: '', price: '', category: 'roadside', description: '' });
                    setShowServiceForm(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Service
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {['roadside', 'mechanic', 'other'].map((category) => {
                    const categoryServices = services.filter(s => s.category === category);
                    if (categoryServices.length === 0) return null;
                    
                    return (
                      <div key={category}>
                        <h3 className="font-semibold text-gray-900 mb-3 capitalize flex items-center gap-2">
                          {category === 'roadside' && <Truck className="w-5 h-5 text-red-600" />}
                          {category === 'mechanic' && <Wrench className="w-5 h-5 text-blue-600" />}
                          {category === 'other' && <Shield className="w-5 h-5 text-gray-600" />}
                          {category} Services
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {categoryServices.map((service) => (
                            <div 
                              key={service.service_type}
                              className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                            >
                              <div className="flex justify-between items-start mb-2">
                                <h4 className="font-medium text-gray-900">{service.name}</h4>
                                <span className="text-lg font-bold text-blue-600">
                                  ${service.price}
                                </span>
                              </div>
                              <p className="text-sm text-gray-500 mb-3">{service.description}</p>
                              <div className="flex gap-2">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => editService(service)}
                                >
                                  <Edit className="w-3 h-3 mr-1" />
                                  Edit
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="text-red-600"
                                  onClick={() => handleDeleteService(service.service_type)}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Service Form Dialog */}
            <Dialog open={showServiceForm} onOpenChange={setShowServiceForm}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingService ? 'Edit Service' : 'Add New Service'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleServiceSubmit} className="space-y-4">
                  <div>
                    <Label>Service ID (lowercase, no spaces)</Label>
                    <Input
                      value={serviceForm.service_type}
                      onChange={(e) => setServiceForm(prev => ({ 
                        ...prev, 
                        service_type: e.target.value.toLowerCase().replace(/\s/g, '_') 
                      }))}
                      placeholder="oil_change"
                      disabled={!!editingService}
                      required
                    />
                  </div>
                  <div>
                    <Label>Display Name</Label>
                    <Input
                      value={serviceForm.name}
                      onChange={(e) => setServiceForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Oil Change"
                      required
                    />
                  </div>
                  <div>
                    <Label>Price ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={serviceForm.price}
                      onChange={(e) => setServiceForm(prev => ({ ...prev, price: e.target.value }))}
                      placeholder="65.00"
                      required
                    />
                  </div>
                  <div>
                    <Label>Category</Label>
                    <Select 
                      value={serviceForm.category} 
                      onValueChange={(v) => setServiceForm(prev => ({ ...prev, category: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="roadside">Roadside Assistance</SelectItem>
                        <SelectItem value="mechanic">Mobile Mechanic</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={serviceForm.description}
                      onChange={(e) => setServiceForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Service description..."
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">
                      {editingService ? 'Update Service' : 'Create Service'}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setShowServiceForm(false)}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>User Management</CardTitle>
                  <CardDescription>Create, edit, and delete user accounts (except owners)</CardDescription>
                </div>
                <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
                  <DialogTrigger asChild>
                    <Button className="bg-blue-600 hover:bg-blue-700">
                      <UserPlus className="w-4 h-4 mr-2" />
                      Create User
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{editingUser ? 'Edit User' : 'Create New User'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={async (e) => {
                      e.preventDefault();
                      try {
                        if (editingUser) {
                          await axios.put(`${API}/admin/users/${editingUser.user_id}`, {
                            name: userForm.name || undefined,
                            email: userForm.email || undefined,
                            phone: userForm.phone || undefined,
                            user_type: userForm.user_type || undefined
                          }, { headers: getAuthHeaders(), withCredentials: true });
                          toast.success('User updated');
                        } else {
                          await axios.post(`${API}/admin/users/create`, userForm, {
                            headers: getAuthHeaders(),
                            withCredentials: true
                          });
                          toast.success('User created');
                        }
                        setShowCreateUser(false);
                        setEditingUser(null);
                        setUserForm({ email: '', password: '', name: '', phone: '', user_type: 'customer', is_admin: false });
                        fetchAllData();
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Failed to save user');
                      }
                    }} className="space-y-4">
                      <div>
                        <Label>Name *</Label>
                        <Input
                          value={userForm.name}
                          onChange={(e) => setUserForm(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="John Doe"
                          required
                        />
                      </div>
                      <div>
                        <Label>Email *</Label>
                        <Input
                          type="email"
                          value={userForm.email}
                          onChange={(e) => setUserForm(prev => ({ ...prev, email: e.target.value }))}
                          placeholder="john@example.com"
                          required={!editingUser}
                        />
                      </div>
                      {!editingUser && (
                        <div>
                          <Label>Password *</Label>
                          <Input
                            type="password"
                            value={userForm.password}
                            onChange={(e) => setUserForm(prev => ({ ...prev, password: e.target.value }))}
                            placeholder="Min 6 characters"
                            required
                          />
                        </div>
                      )}
                      <div>
                        <Label>Phone</Label>
                        <Input
                          value={userForm.phone}
                          onChange={(e) => setUserForm(prev => ({ ...prev, phone: e.target.value }))}
                          placeholder="(555) 123-4567"
                        />
                      </div>
                      <div>
                        <Label>User Type</Label>
                        <Select value={userForm.user_type} onValueChange={(v) => setUserForm(prev => ({ ...prev, user_type: v }))}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="customer">Customer</SelectItem>
                            <SelectItem value="mechanic">Mechanic</SelectItem>
                            <SelectItem value="roadside_tech">Roadside Tech</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <DialogFooter className="gap-2">
                        <Button type="button" variant="outline" onClick={() => {
                          setShowCreateUser(false);
                          setEditingUser(null);
                          setUserForm({ email: '', password: '', name: '', phone: '', user_type: 'customer', is_admin: false });
                        }}>
                          Cancel
                        </Button>
                        <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                          {editingUser ? 'Update User' : 'Create User'}
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {users.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No users found</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="text-left py-3 px-2">Name</th>
                          <th className="text-left py-3 px-2">Email</th>
                          <th className="text-left py-3 px-2">Type</th>
                          <th className="text-left py-3 px-2">Status</th>
                          <th className="text-left py-3 px-2">Joined</th>
                          <th className="text-left py-3 px-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((u) => (
                          <tr key={u.user_id} className={`border-b hover:bg-gray-50 ${u.is_owner ? 'bg-purple-50' : ''}`}>
                            <td className="py-2 px-2 font-medium">
                              {u.name}
                              {u.is_owner && <Badge className="ml-2 bg-purple-600 text-white text-xs">OWNER</Badge>}
                              {u.is_admin && !u.is_owner && <Badge className="ml-2 bg-indigo-600 text-white text-xs">ADMIN</Badge>}
                            </td>
                            <td className="py-2 px-2 text-gray-600 truncate max-w-[200px]">{u.email}</td>
                            <td className="py-2 px-2">
                              <Badge className={
                                u.user_type === 'customer' ? 'bg-blue-100 text-blue-700' :
                                u.user_type === 'mechanic' ? 'bg-green-100 text-green-700' :
                                u.user_type === 'roadside_tech' ? 'bg-orange-100 text-orange-700' :
                                u.user_type === 'admin' ? 'bg-indigo-100 text-indigo-700' :
                                u.user_type === 'owner' ? 'bg-purple-100 text-purple-700' :
                                'bg-gray-100 text-gray-700'
                              }>
                                {u.user_type}
                              </Badge>
                            </td>
                            <td className="py-2 px-2">
                              <Badge className={u.is_active === false ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}>
                                {u.is_active === false ? 'Inactive' : 'Active'}
                              </Badge>
                            </td>
                            <td className="py-2 px-2 text-gray-600">
                              {new Date(u.created_at).toLocaleDateString()}
                            </td>
                            <td className="py-2 px-2">
                              {/* Can't modify owner accounts or self */}
                              {u.user_id !== user.user_id && !u.is_owner ? (
                                <div className="flex gap-1">
                                  {/* Edit User */}
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setEditingUser(u);
                                      setUserForm({
                                        email: u.email,
                                        password: '',
                                        name: u.name,
                                        phone: u.phone || '',
                                        user_type: u.user_type,
                                        is_admin: u.is_admin
                                      });
                                      setShowCreateUser(true);
                                    }}
                                    title="Edit User"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>

                                  {/* Delete User */}
                                  <Dialog>
                                    <DialogTrigger asChild>
                                      <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" title="Delete User">
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </DialogTrigger>
                                    <DialogContent>
                                      <DialogHeader>
                                        <DialogTitle className="text-red-600">Delete User?</DialogTitle>
                                      </DialogHeader>
                                      <p className="text-gray-600">
                                        Are you sure you want to delete <strong>{u.name}</strong> ({u.email})?
                                      </p>
                                      <p className="text-sm text-red-500">This action cannot be undone.</p>
                                      <DialogFooter className="gap-2">
                                        <Button variant="outline">Cancel</Button>
                                        <Button 
                                          variant="destructive"
                                          onClick={async () => {
                                            try {
                                              await axios.delete(`${API}/admin/users/${u.user_id}`, {
                                                headers: getAuthHeaders(),
                                                withCredentials: true
                                              });
                                              toast.success(`User ${u.name} deleted`);
                                              fetchAllData();
                                            } catch (error) {
                                              toast.error(error.response?.data?.detail || 'Failed to delete user');
                                            }
                                          }}
                                        >
                                          Delete
                                        </Button>
                                      </DialogFooter>
                                    </DialogContent>
                                  </Dialog>
                                </div>
                              ) : (
                                <span className="text-gray-400 text-sm">{u.is_owner ? 'Owner' : 'You'}</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <Card>
              <CardHeader>
                <CardTitle>Activity Log</CardTitle>
              </CardHeader>
              <CardContent>
                {notifications.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No activity logged yet</p>
                ) : (
                  <div className="space-y-3">
                    {notifications.map((notif) => (
                      <div 
                        key={notif.notification_id}
                        className="p-4 bg-gray-50 rounded-lg flex items-start gap-4"
                      >
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          notif.type === 'registration' ? 'bg-blue-100' :
                          notif.type === 'login' ? 'bg-green-100' :
                          notif.type === 'new_application' ? 'bg-yellow-100' :
                          notif.type === 'new_request' ? 'bg-purple-100' :
                          'bg-gray-100'
                        }`}>
                          {notif.type === 'registration' && <Users className="w-5 h-5 text-blue-600" />}
                          {notif.type === 'login' && <Check className="w-5 h-5 text-green-600" />}
                          {notif.type === 'new_application' && <FileText className="w-5 h-5 text-yellow-600" />}
                          {notif.type === 'new_request' && <Car className="w-5 h-5 text-purple-600" />}
                          {!['registration', 'login', 'new_application', 'new_request'].includes(notif.type) && 
                            <Bell className="w-5 h-5 text-gray-600" />}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{notif.subject}</p>
                          <p className="text-sm text-gray-600 whitespace-pre-wrap">{notif.message}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(notif.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Live Provider Map Tab */}
          <TabsContent value="provider-map">
            <LiveProviderMap />
          </TabsContent>

          {/* Provider Management Tab */}
          <TabsContent value="provider-mgmt">
            <ProviderManagement />
          </TabsContent>

          {/* Ad Approval Tab */}
          <TabsContent value="ad-approval">
            <AdminAdApproval />
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <AdminOrderManagement />
          </TabsContent>
          
          <TabsContent value="payouts">
            <PayoutApprovalDashboard isOwner={false} />
          </TabsContent>
          
          {/* Auto Parts Order Approval Tab */}
          <TabsContent value="auto-parts">
            <AutoPartsOrderApproval />
          </TabsContent>
          
          {/* AI Food & Auto Scraper Tab */}
          <TabsContent value="food-scraper">
            <FoodDeliveryAdmin />
          </TabsContent>

          {/* Webhook Queue Monitor Tab */}
          <TabsContent value="webhooks">
            <WebhookQueueMonitor />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
